import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, message } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/en";
import { useLocalStorage } from "../../../../hooks";
import { useTimeZone, getTimezoneByMode, TIMEZONE_OPTIONS } from "../../../../contexts/TimeZoneContext";
import { resolveTime } from "../../../../utils/resolveTime";
import { displayFlightDateByTimezone } from "../../../../utils/timezoneUtils";
import { writeExcelFriendlyTableToClipboard, sortFlightsWithRotationSeparators } from "../../../../utils/clipboardTableUtils";

dayjs.extend(utc);
dayjs.extend(timezone);

export const DEFAULT_FLIGHT_COPY_COLUMNS = [
    { key: "date", label: "Date", enabled: true },
    { key: "aircraft_reg", label: "A/C REG", enabled: true },
    { key: "day", label: "Day", enabled: true },
    { key: "flight_number", label: "Flight", enabled: true },
    { key: "departure_airport", label: "DEP", enabled: true },
    { key: "arrival_airport", label: "ARR", enabled: true },
    { key: "std", label: "STD", enabled: true },
    { key: "sta", label: "STA", enabled: true },
    { key: "stc_type", label: "STC", enabled: true },
    { key: "block_hours", label: "BH", enabled: true },
    { key: "ground_time", label: "GT", enabled: true },
];

export const DEFAULT_FLIGHT_COPY_EXTRA_COLUMNS = [
    { key: "etd", label: "ETD", enabled: false },
    { key: "eta", label: "ETA", enabled: false },
    { key: "atd", label: "ATD", enabled: false },
    { key: "ata", label: "ATA", enabled: false },
    { key: "status", label: "Status", enabled: false },
    { key: "remarks", label: "Remarks", enabled: false },
];

const STATUS_LABELS = {
    SCHEDULED: "已计划",
    DELAYED: "延误",
    DEPARTED: "已起飞",
    COMPLETED: "已完成",
    CANCELLED: "已取消",
    PLAN_CANCELLED: "计划取消",
    DIVERTED: "备降",
    REPLACED: "被替换",
};

const useFlightCopy = ({ defaultColumns = DEFAULT_FLIGHT_COPY_COLUMNS, extendedColumns = DEFAULT_FLIGHT_COPY_EXTRA_COLUMNS, copyTitle = "AAE SCHEDULE (UTC TIME)", flights = [], selectedRowKeys = [], airportsMap = {} }) => {
    const { timeZone: timeZoneMode, airportsMap: timeZoneAirportsMap } = useTimeZone();
    const [copyConfigModalVisible, setCopyConfigModalVisible] = useState(false);
    const [selectedCopyColumns, setSelectedCopyColumns] = useLocalStorage("flymeta_copy_columns", []);
    const [includeTitleInCopy, setIncludeTitleInCopy] = useLocalStorage("flymeta_copy_include_title", true);
    // 默认开为原设计（这个复制入口的主产物是轮档单）；沿用旧存储键，此前手动关掉的人保持关
    const [showRotationSeparator, setShowRotationSeparator] = useLocalStorage("flymeta_copy_rotation_separator", true);

    const defaultCopyColumnKeys = useMemo(() => defaultColumns.filter((col) => col.enabled).map((col) => col.key), [defaultColumns]);

    const extendedCopyColumns = useMemo(() => [...defaultColumns, ...extendedColumns], [defaultColumns, extendedColumns]);

    useEffect(() => {
        if (selectedCopyColumns.length === 0) {
            setSelectedCopyColumns(defaultCopyColumnKeys);
        }
    }, [defaultCopyColumnKeys, selectedCopyColumns.length, setSelectedCopyColumns]);

    const formatFlightDataForCopy = useCallback(
        (flight, columns) => {
            const formatValue = (key, flight) => {
                switch (key) {
                    case "date": {
                        // 与日期格子（FlightDateDisplay）共用同一套换算函数：ETD 优先，其次 STD，换算到当前所选时区
                        const departureInstant = flight.etd || flight.std;
                        if (departureInstant) {
                            const targetTimezone = getTimezoneByMode(timeZoneMode, flight.departure_airport, timeZoneAirportsMap);
                            return displayFlightDateByTimezone(departureInstant, targetTimezone, "DD-MMM", "en").toUpperCase();
                        }
                        // 缺起飞时刻才退回航班日：与格子相同，按世界时写原文，不编造起飞时刻再换算
                        if (flight.flight_date) {
                            return displayFlightDateByTimezone(flight.flight_date, TIMEZONE_OPTIONS.UTC, "DD-MMM", "en").toUpperCase();
                        }
                        return "";
                    }
                    case "aircraft_reg":
                        return flight.aircraft_reg || "";
                    case "day":
                        if (flight.etd || flight.std || flight.flight_date) {
                            const timeToConvert = flight.etd || flight.std || flight.flight_date;
                            try {
                                const targetTimezone = getTimezoneByMode(timeZoneMode, flight.departure_airport, airportsMap);
                                const dayOfWeek = dayjs.utc(timeToConvert).tz(targetTimezone).day();
                                return dayOfWeek === 0 ? 7 : dayOfWeek;
                            } catch (error) {
                                return "";
                            }
                        }
                        return "";
                    case "flight_number":
                        return flight.flight_number || "";
                    case "departure_airport":
                        return flight.departure_airport || "";
                    case "arrival_airport":
                        return flight.arrival_airport || "";
                    case "std":
                        return flight.effective_departure_time ? dayjs.utc(flight.effective_departure_time).format("HH:mm") : "";
                    case "sta":
                        return flight.effective_arrival_time ? dayjs.utc(flight.effective_arrival_time).format("HH:mm") : "";
                    case "etd":
                        return flight.etd ? dayjs.utc(flight.etd).format("HH:mm") : "";
                    case "eta":
                        return flight.eta ? dayjs.utc(flight.eta).format("HH:mm") : "";
                    case "atd": {
                        const rAtd = resolveTime(flight, "atd");
                        return rAtd.value ? dayjs.utc(rAtd.value).format("HH:mm") : "";
                    }
                    case "ata": {
                        const rAta = resolveTime(flight, "ata");
                        return rAta.value ? dayjs.utc(rAta.value).format("HH:mm") : "";
                    }
                    case "stc_type":
                        return flight.stc_type || "";
                    case "block_hours":
                        if (flight.effective_departure_time && flight.effective_arrival_time) {
                            const duration = dayjs.utc(flight.effective_arrival_time).diff(dayjs.utc(flight.effective_departure_time), "minute");
                            const hours = Math.floor(duration / 60);
                            const minutes = duration % 60;
                            return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
                        }
                        return "";
                    case "ground_time":
                        if (flight.actual_ground_time_minutes) {
                            const hours = Math.floor(flight.actual_ground_time_minutes / 60);
                            const minutes = flight.actual_ground_time_minutes % 60;
                            if (hours > 0 && minutes > 0) {
                                return `${hours}h${minutes}m`;
                            } else if (hours > 0) {
                                return `${hours}h`;
                            } else {
                                return `${minutes}m`;
                            }
                        }
                        return "";
                    case "status":
                        return STATUS_LABELS[flight.status] || flight.status || "";
                    case "remarks":
                        return flight.remarks || "";
                    default:
                        return "";
                }
            };

            return columns.map((columnKey) => formatValue(columnKey, flight));
        },
        [timeZoneMode, airportsMap, timeZoneAirportsMap],
    );

    const handleOpenCopyConfig = useCallback(() => {
        setCopyConfigModalVisible(true);
    }, []);

    const handleCloseCopyConfig = useCallback(() => {
        setCopyConfigModalVisible(false);
    }, []);

    const handleCopyColumnChange = useCallback(
        (checkedValues) => {
            setSelectedCopyColumns(checkedValues);
        },
        [setSelectedCopyColumns],
    );

    const handleCopyFlights = useCallback(
        async (useCustomColumns = false) => {
            if (selectedRowKeys.length === 0) {
                message.warning("请先选择要复制的航班");
                return;
            }

            const selectedFlights = flights.filter((flight) => selectedRowKeys.includes(flight.id));
            const columnsToUse = useCustomColumns ? selectedCopyColumns : defaultCopyColumnKeys;

            const columnLabels = columnsToUse.map((columnKey) => {
                const columnConfig = extendedCopyColumns.find((col) => col.key === columnKey);
                return columnConfig ? columnConfig.label : columnKey;
            });

            const sorted = showRotationSeparator ? sortFlightsWithRotationSeparators(selectedFlights) : selectedFlights;
            const formattedData = sorted.map((item) => (item === null ? columnsToUse.map(() => "") : formatFlightDataForCopy(item, columnsToUse)));

            const tableRows = [columnLabels, ...formattedData];

            if (includeTitleInCopy) {
                tableRows.unshift([copyTitle]);
            }

            try {
                await writeExcelFriendlyTableToClipboard(tableRows);
                const missingCount = selectedRowKeys.length - selectedFlights.length;
                const successText = `已复制 ${selectedFlights.length} 个航班的数据到剪贴板`;
                message.success(missingCount > 0 ? `${successText}；另有 ${missingCount} 个已勾选航班不在当前页，未包含在内` : successText);
            } catch (error) {
                console.error("复制到剪贴板失败:", error);
                message.error("复制失败，请检查浏览器权限");
            }
        },
        [selectedRowKeys, flights, selectedCopyColumns, defaultCopyColumnKeys, extendedCopyColumns, showRotationSeparator, formatFlightDataForCopy, includeTitleInCopy, copyTitle],
    );

    const copyMenuItems = useMemo(
        () => [
            {
                key: "default",
                label: "复制默认列",
                onClick: () => handleCopyFlights(false),
            },
            {
                key: "custom",
                label: "复制自定义列",
                onClick: () => handleCopyFlights(true),
            },
            {
                type: "divider",
            },
            {
                key: "include_title",
                label: (
                    <Checkbox checked={includeTitleInCopy} onChange={(e) => setIncludeTitleInCopy(e.target.checked)} onClick={(e) => e.stopPropagation()}>
                        包含标题
                    </Checkbox>
                ),
            },
            {
                key: "rotation_separator",
                label: (
                    <Checkbox checked={showRotationSeparator} onChange={(e) => setShowRotationSeparator(e.target.checked)} onClick={(e) => e.stopPropagation()}>
                        Rotation 空行
                    </Checkbox>
                ),
            },
            {
                type: "divider",
            },
            {
                key: "configure",
                label: "配置复制列",
                onClick: handleOpenCopyConfig,
            },
        ],
        [handleCopyFlights, handleOpenCopyConfig, includeTitleInCopy, setIncludeTitleInCopy, showRotationSeparator, setShowRotationSeparator],
    );

    return {
        copyConfigModalVisible,
        setCopyConfigModalVisible,
        selectedCopyColumns,
        setSelectedCopyColumns,
        includeTitleInCopy,
        setIncludeTitleInCopy,
        showRotationSeparator,
        setShowRotationSeparator,
        defaultCopyColumns: defaultColumns,
        extendedCopyColumns,
        formatFlightDataForCopy,
        handleCopyFlights,
        handleOpenCopyConfig,
        handleCloseCopyConfig,
        handleCopyColumnChange,
        copyMenuItems,
    };
};

export default useFlightCopy;
